// Shared row primitive for the capture checklist. Used by the in-lead
// `ChecklistCard` (slice B1: click-to-focus) and — once the dock-mode
// plumbing lands — by `ActivityDockActiveLeadChecklistSection`.
//
// Two render modes:
//   - When `onClick` is supplied, the row renders as a `<button>` and
//     missing/warning rows pulse with the slice-B1 amber halo so they
//     read as actionable.
//   - When `onClick` is omitted, the row renders as a passive `<li>`
//     content — visually identical, no interaction affordance.
//
// The flash-on-flip-to-ok behavior continues to live in the consumer
// (the dock section owns the "newly ok" detection because only it has
// the previous-status snapshot).
//
// Slice B1 of the v1.0 Lead Control MVP polish pass — see
// `docs/planning/lead_control_mvp_polish.md`.

import type {
  LeadChecklistItem,
  LeadChecklistItemKey,
  LeadChecklistItemStatus,
} from "./leadChecklistItems";

export type LeadChecklistRowProps = {
  item: LeadChecklistItem;
  /**
   * Optional click handler. When provided AND `item.status` is
   * not "ok", the row becomes a clickable button with the missing-pulse
   * animation. When omitted the row is passive (matches the legacy
   * `ChecklistCard` rendering).
   */
  onClick?: (key: LeadChecklistItemKey) => void;
  /**
   * Tailwind classes for the per-status text color. Required so the
   * caller can opt into either the legacy three-color set
   * (`text-red-600` / `text-yellow-600` / `text-green-600`) or the
   * dock-style set (`text-rose-600` etc.) without LeadChecklistRow
   * needing to know about either palette.
   */
  statusColorClass: string;
  /** Extra classes (e.g. the flash-to-ok animation) merged onto the row. */
  extraClass?: string;
};

const ACTIONABLE_STATUSES: ReadonlySet<LeadChecklistItemStatus> = new Set([
  "missing",
  "warning",
]);

export function LeadChecklistRow(props: LeadChecklistRowProps) {
  const { item, onClick, statusColorClass, extraClass } = props;

  const isActionable = !!onClick && ACTIONABLE_STATUSES.has(item.status);
  const pulseClass = isActionable ? "lead-checklist-item-missing-pulse" : "";

  const className = [statusColorClass, pulseClass, extraClass]
    .filter(Boolean)
    .join(" ");

  if (!onClick) {
    return <li className={className}>{item.label}</li>;
  }

  // When clickable, render an inline button inside the `<li>` so the
  // parent `<ul>` markup stays valid (list-disc bullets render against
  // the `<li>`, not the button). The button stretches full-width so the
  // whole row remains a comfortable click target.
  return (
    <li className={className}>
      <button
        type="button"
        onClick={() => onClick(item.key)}
        className="block w-full text-left text-inherit hover:underline focus:outline-none focus-visible:underline"
        aria-label={`${item.label} — click to focus the related field`}
      >
        {item.label}
      </button>
    </li>
  );
}
